import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// ── Types ────────────────────────────────────────────────────────────────────

export interface CreateUserPayload {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  role: string;
  password: string;
}

export interface UserCreatedResponse {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
}

export interface KeycloakUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
  contactNumber?: string;
  bio?: string;
}

export interface KeycloakPagedUsers {
  content: KeycloakUser[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

/**
 * A typed error that carries a user-friendly message extracted from either:
 *  - Spring ProblemDetail (`detail` field)
 *  - A plain `message` / `error` field
 *  - A generic fallback depending on HTTP status
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly isNetworkError: boolean,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class KeycloakApiService {
  private readonly http = inject(HttpClient);

  // ── Availability checks ───────────────────────────────────────────────────

  /** Returns true if the username is already registered. Throws ApiError on any failure. */
  checkUsernameExists(username: string): Observable<boolean> {
    return this.http
      .get<{ exists: boolean }>(`/api/keycloak/users/exists/username/${encodeURIComponent(username)}`)
      .pipe(
        map(r => r.exists),
        catchError(err => throwError(() => this.toApiError(err))),
      );
  }

  /** Returns true if the email is already registered. Throws ApiError on any failure. */
  checkEmailExists(email: string): Observable<boolean> {
    return this.http
      .get<{ exists: boolean }>(`/api/keycloak/users/exists/email?email=${encodeURIComponent(email)}`)
      .pipe(
        map(r => r.exists),
        catchError(err => throwError(() => this.toApiError(err))),
      );
  }

  // ── User creation ─────────────────────────────────────────────────────────

  /** Creates a user in Keycloak. Throws ApiError with a friendly message on failure. */
  createUser(payload: CreateUserPayload): Observable<UserCreatedResponse> {
    return this.http
      .post<UserCreatedResponse>('/api/keycloak/users', payload)
      .pipe(
        catchError(err => throwError(() => this.toApiError(err))),
      );
  }

  /** Returns the full Keycloak profile for the currently authenticated user. */
  getMe(): Observable<KeycloakUser> {
    return this.http
      .get<KeycloakUser>('/api/keycloak/users/me')
      .pipe(
        catchError(err => throwError(() => this.toApiError(err))),
      );
  }

  /** Updates firstName, lastName, email and username for the given user id. */
  updateProfile(userId: string, payload: { firstName: string; lastName: string; email: string; username: string; contactNumber?: string; bio?: string }): Observable<void> {
    return this.http
      .put<void>(`/api/keycloak/users/${encodeURIComponent(userId)}`, payload)
      .pipe(
        catchError(err => throwError(() => this.toApiError(err))),
      );
  }

  /**
   * Returns a paginated list of Keycloak users assigned to a given realm role.
   * @param roleName  Keycloak realm role name (e.g. 'ADMIN', 'STUDENT')
   * @param first     Zero-based offset (page * size)
   * @param max       Page size
   */
  getUsersByRole(roleName: string, first: number = 0, max: number = 10): Observable<KeycloakPagedUsers> {
    return this.http
      .get<KeycloakPagedUsers>(`/api/keycloak/users/role/${encodeURIComponent(roleName)}`, {
        params: { first: first.toString(), max: max.toString() }
      })
      .pipe(
        catchError(err => throwError(() => this.toApiError(err))),
      );
  }

  /** Enables or disables a Keycloak user. */
  toggleUserEnabled(userId: string, enabled: boolean): Observable<void> {
    return this.http
      .patch<void>(`/api/keycloak/users/${encodeURIComponent(userId)}/enabled`, null, {
        params: { enabled: enabled.toString() }
      })
      .pipe(
        catchError(err => throwError(() => this.toApiError(err))),
      );
  }

  /** Permanently deletes a user from Keycloak. */
  deleteKeycloakUser(userId: string): Observable<void> {
    return this.http
      .delete<void>(`/api/keycloak/users/${encodeURIComponent(userId)}`)
      .pipe(
        catchError(err => throwError(() => this.toApiError(err))),
      );
  }

  // ── Error normalisation ───────────────────────────────────────────────────

  private toApiError(err: unknown): ApiError {
    if (!(err instanceof HttpErrorResponse)) {
      return new ApiError('An unexpected error occurred.', 0, false);
    }

    const status = err.status;

    // status 0 → browser could not reach the server (offline, CORS preflight blocked, etc.)
    if (status === 0) {
      return new ApiError(
        'Unable to reach the server. Please check your connection and try again.',
        0,
        true,
      );
    }

    // Extract the human-readable detail from Spring ProblemDetail or fallback fields
    const body = err.error;
    const detail: string =
      body?.detail ?? body?.message ?? body?.error ?? this.statusFallback(status);

    return new ApiError(detail, status, false);
  }

  private statusFallback(status: number): string {
    switch (status) {
      case 400: return 'Invalid request. Please check your inputs.';
      case 401: return 'You are not authorised to perform this action.';
      case 403: return 'Access denied.';
      case 404: return 'Resource not found.';
      case 409: return 'A conflict occurred. The username or email may already be in use.';
      case 422: return 'Validation failed. Please check your inputs.';
      case 502:
      case 503: return 'The service is temporarily unavailable. Please try again shortly.';
      default:  return 'Something went wrong. Please try again.';
    }
  }
}
